import React from 'react'
import Img from 'gatsby-image'
import styled, { css } from 'styled-components'
import { Variables } from '../styles/Variables'
import Button from './Button'
import Container from './Container'
import { BodyCopy, H2 } from './Typography'
import Slideshow from './Slideshow'
import media from 'css-in-js-media'
import { SlideshowSlideWrapper } from './SlideshowSlider'
import { StyledButton } from './ButtonStyled'
import AnimatedSection from './AnimatedSection'

const InnerWrap = styled.div`
    display: flex;
    position: relative;
    z-index: 1;
    ${media('<desktop')} {
        flex-direction: column;
    }
`

const ImageWrap = styled.div`
    box-sizing: border-box;
    position: relative;
    img {
        max-width: 100%;
    }
    ${SlideshowSlideWrapper} {
        padding-top: 100%;
    }
`

const Content = styled.div`
    width: 100%;
    box-sizing: border-box;
    padding: 40px 30px 0;
    display: flex;
    align-items: center;
    justify-content: center;

    ${media('>=desktop')} {
        width: 50%;
        padding: 20px;
    }

    ${H2} {
        margin: 0 0 20px;
    }
`

const ContentInner = styled.div`
    ${H2} {
        font-family: ${Variables.font.body};
    }
`

const themePrimary = css`
    ${Content} {
        background-color: ${Variables.color.pazolaKarry};
        color: ${Variables.color.pazola};
    }
`

const themeWhite = css`
    ${Content} {
        background-color: ${Variables.color.white};
        color: ${Variables.color.pazola};
    }
`

const themeOffWhite = css`
    ${Content} {
        background-color: ${Variables.color.lightGrey};
        color: ${Variables.color.pazola};
    }
`

const Layout1 = css`
    ${ImageWrap} {
        width: 100%;
        ${media('<desktop')} {
            padding: 0 30px;
        }
        ${media('>=desktop')} {
            width: 50%;
        }
    }
    ${Content} {
        padding: 0 30px 20px;
        ${media('>=desktop')} {
            padding: 0;
        }
    }
    ${InnerWrap} {
        ${media('>=desktop')} {
            justify-content: space-between;
        }
    }
`

const Layout2 = css`
    ${ImageWrap} {
        ${media('>=desktop')} {
            width: calc(50% + 260px);
            height: 100%;
            box-sizing: border-box;
            margin-left: -260px;
            display: flex;
            align-items: center;
            z-index: -1;
        }
    }
    .gatsby-image-wrapper {
        width: 100%;
    }
`

// Stacked elements. Used in 2XImageCopy
const Layout3 = css`
    ${media('>=desktop')} {
        text-align: center;
    }

    ${ImageWrap} {
        width: 100%;
    }

    ${InnerWrap} {
        flex-direction: column;
    }
    ${Content} {
        width: 100%;
        ${media('>=desktop')} {
            padding: 40px 0 0;
        }
    }
    ${ContentInner} {
        ${media('>=desktop')} {
            padding: 0 40px;
        }
    }
`

const alignLeft = css`
    ${Content} {
        ${media('>=desktop')} {
            padding-left: 120px;
        }
    }
`

const alignRight = css`
    ${InnerWrap} {
        flex-direction: row-reverse;
        ${media('<desktop')} {
            flex-direction: column;
        }
    }
`

const FullWidthLayout = css`
    padding: 0;
    margin: 0;
        
    ${Container} {
        padding-left: 0;
        padding-right: 0;
    }

    ${ImageWrap} {
        ${media('<desktop')} {
            padding: 0;
        }
    }

    ${Content} {
        padding: 60px;
        ${media('<desktop')} {
            padding: 56px 20px;
        }
        ${media('>=desktop')} {
            box-sizing: border-box;
        }

        ${H2} {
            font-family: ${Variables.font.body};
            font-style: normal;
            font-weight: 600;
            font-size: 16px;
            line-height: 100%;
            letter-spacing: 0.1em;
            text-transform: uppercase;
            margin-bottom: 30px;
            ${media('<desktop')} {
                font-size: 12px;
                line-height: 130%;
            }
        }

        ${BodyCopy} {
            font-family: ${Variables.font.title};
            font-style: normal;
            font-weight: 300;
            font-size: 38px;
            line-height: 44px;
            ${media('<desktop')} {
                font-size: 24px;
                line-height: 30px;
            }
            blockquote {
                padding: 155px 73px;
                font-family: ${Variables.font.title};
                font-style: normal;
                font-weight: 300;
                font-size: 32px;
                line-height: 37px;
                color: ${Variables.color.pazola};
                margin: 0;
                display: flex;
                ${media('<desktop')} {
                    padding: 0;
                    font-size: 24px;
                    line-height: 30px;
                }

                p {
                    font-family: inherit;
                    font-style: inherit;
                    font-weight: inherit;
                    font-size: inherit;
                    line-height: inherit;
                    color: inherit;
                    margin: 0;
                    display: flex;
                    flex-direction: column;
                    width: 100%;
                    letter-spacing: inherit;

                    strong {
                        font-family: ${Variables.font.body};
                        font-style: normal;
                        font-weight: 500;
                        font-size: 16px;
                        line-height: 20px;
                        margin-top: 40px;
                        ${media('<desktop')} {
                            margin-top: 20px;
                        }
                    }
                }
            }
            hr {
                height: 2px;
                background-color: ${Variables.color.pazolaGold};
                border: none;
                line-height: 5px;
                margin: 44px 0;
                ::after {
                    content: '';
                    width: 100%;
                    height: 2px;
                    background-color: ${Variables.color.pazolaGold};
                    display: inline-block;
                }
                ${media('<desktop')} {
                    margin: 50px 0;
                }
            }
            p {
                font-family: ${Variables.font.body};                
                font-size: 18px;
                line-height: 22px;
                letter-spacing: 0.1em;
                ${media('<desktop')} {
                    font-size: 16px;
                    line-height: 20px;
                }
                strong {
                    font-family: ${Variables.font.bodyBold};
                }
            }
            h2 {
                font-family: ${Variables.font.title};
                font-style: normal;
                font-weight: 300;
                font-size: 38px;
                line-height: 44px;
                ${media('<desktop')} {
                    font-size: 24px;
                    line-height: 30px;
                }
            }
        }

        ${StyledButton} {
            background-color: transparent;
            border-color: ${Variables.color.pazola};
            &::before {
                border-color: ${Variables.color.pazola};
            }
            &:hover {
                background-color: ${Variables.color.pazolaGold};
                border-color: ${Variables.color.white} ;
                &::before {
                    border-color: ${Variables.color.white};
                }
            }
            ${media('<desktop')} {
                width: max-content;
            }
        }
    }
`

const FullWidthLayoutLeft = css`
    ${Content} {
        ${media('>=desktop')} {
            /* padding: 0px 5.6vw 0px 0; */
        }
    }
`

const FullWidthLayoutRight = css`
    ${Content} {
        ${media('>=desktop')} {
            /* padding: 0px 0px 0px 5.6vw; */
        }
    }
`

const Wrapper = styled.section`
    ${Content} {
        align-items: ${(props) => (props.$copyVerticalAlignment == 'Middle' ? 'center' : 'end')};
    }

    ${themeWhite}
    ${(props) => props.theme == 'primary' && themePrimary}
    ${(props) => props.theme == 'white' && themeWhite}
    ${(props) => props.theme == 'offwhite' && themeOffWhite}

    ${(props) => props.align == 'left' && alignLeft}
    ${(props) => props.align == 'right' && alignRight}

    ${(props) =>
        props.fullWidth == 'No' &&
        css`
            padding-top: 40px;
            padding-bottom: 40px;

            ${media('>=desktop')} {
                padding-top: 0px;
                padding-bottom: 0px;
            }
        `}

    ${(props) => props.align == 'left' && props.fullWidth == 'No' && css``}

    ${(props) =>
        props.align == 'right' &&
        props.fullWidth == 'No' &&
        css`
            ${Content} {
                ${media('>=desktop')} {
                    justify-content: flex-start;
                }
            }
            ${ContentInner} {
                ${media('>=desktop')} {
                    padding: 0px 0px 0px 5.6vw;
                }
            }

            ${InnerWrap} {
                ${media('<desktop')} {
                    flex-direction: column-reverse;
                }
            }
        `}

    ${(props) =>
        (props.align == 'right' && props.fullWidth != 'No') ||
        (props.fullWidth == null &&
            css`
                ${ContentInner} {
                    ${media('>=desktop')} {
                        /* margin-left: 5vw; */
                    }
                }
            `)}

    ${Container} {
        /* ${media('<desktop')} { */
        padding-left: 0;
        padding-right: 0;
        /* } */
    }

    ${({ layout }) => (layout == '3' ? Layout3 : layout == '2' ? Layout2 : Layout1)}

    ${({ fullWidth }) => (fullWidth == null || fullWidth == 'Yes') && FullWidthLayout} 
    ${({ fullWidth, align }) => (fullWidth == null || fullWidth == 'Yes') && align == 'left' && FullWidthLayoutLeft}
    ${({ fullWidth, align }) => (fullWidth == null || fullWidth == 'Yes') && align == 'right' && FullWidthLayoutRight}
`

const Buttons = styled.div`
    margin-top: 30px;

    ${media('>=desktop')} {
        white-space: nowrap;
    }
    ${StyledButton} {
        ${media('<desktop')} {
            display: block;
            width: 100%;
        }
    }
    ${StyledButton} + ${StyledButton} {
        ${media('>=desktop')} {
            margin-left: 20px;
        }
        ${media('<desktop')} {
            margin-top: 20px;
        }
    }
`

const ImageCaptionBlock = styled(BodyCopy)`
    margin-top: 40px;
    color: ${Variables.color.cocoa};
    letter-spacing: 0.0025em;
    font-style: normal;
    font-weight: 300;
    font-size: 18px;
    line-height: 26px;
`

const ImageCopy: React.FC<{
    data: object
}> = ({ data }) => {
    const {
        theme,
        alignment,
        layout,
        fullWidth,
        slideshow,
        image,
        imageCaption,
        title,
        copy,
        buttons,
        copyVerticalAlignment,
    } = data
    return (
        <Wrapper
            $copyVerticalAlignment={copyVerticalAlignment}
            theme={theme}
            align={alignment}
            layout={layout}
            fullWidth={fullWidth}
            className="ImageCopy"
        >
            <Container>
                <InnerWrap>
                    <ImageWrap>
                        {slideshow ? (
                            <Slideshow data={slideshow} fullWidth={true} />
                        ) : (
                            image && <Img fluid={image.fluid} alt="" />
                        )}
                        {imageCaption && (
                            <ImageCaptionBlock
                                dangerouslySetInnerHTML={{
                                    __html: imageCaption.childMarkdownRemark.html,
                                }}
                            />
                        )}
                    </ImageWrap>

                    <Content>
                        <AnimatedSection>
                            <ContentInner>
                                {title && <H2>{title}</H2>}
                                {copy && (
                                    <BodyCopy
                                        dangerouslySetInnerHTML={{
                                            __html: copy.childMarkdownRemark.html,
                                        }}
                                    />
                                )}
                                {buttons && (
                                    <Buttons>
                                        {buttons.map((button, i) => {
                                            const buttonTheme =
                                                button.theme == 'secondary' ? { secondary: true } : { primary: true }
                                            return (
                                                <Button key={i} buttonLink={button.buttonLink} {...buttonTheme}>
                                                    {button.text}
                                                </Button>
                                            )
                                        })}
                                    </Buttons>
                                )}
                            </ContentInner>
                        </AnimatedSection>
                    </Content>
                </InnerWrap>
            </Container>
        </Wrapper>
    )
}

export default ImageCopy
